// Ctrl/Cmd+K (or "/") command palette: jump anywhere, search the marketplace, open accessibility settings.
// "?" shows keyboard shortcuts; "g" then a letter jumps straight to a section.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Home, Store, Clapperboard, Newspaper, Trophy, Plane, Map, Smartphone, Sparkles, Users, CalendarCheck, User, Briefcase,
  BarChart3, Megaphone, Info, CreditCard, Mail, Accessibility, Keyboard, Search, Bot, Luggage, Lock, Ticket,
} from "lucide-react";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, CommandShortcut } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const GROUPS = [
  { label: "Explore", items: [
    { label: "Home", to: "/", icon: Home, keys: "g h" },
    { label: "Marketplace", to: "/marketplace", icon: Store, keys: "g m" },
    { label: "Stage", to: "/stage", icon: Clapperboard, keys: "g s" },
    { label: "News", to: "/stage/news", icon: Newspaper },
    { label: "Sports", to: "/stage/sports", icon: Trophy },
    { label: "Travel", to: "/travel", icon: Plane, keys: "g t" },
    { label: "Plan a trip", to: "/travel/plan", icon: Map },
    { label: "Travel eSIMs", to: "/travel/esims", icon: Smartphone },
    { label: "Yasmin, your assistant", to: "/yasmin", icon: Sparkles },
    { label: "Your agents", to: "/agents", icon: Bot },
    { label: "Creators", to: "/creators", icon: Users },
  ] },
  { label: "Your account", items: [
    { label: "Bookings", to: "/bookings", icon: CalendarCheck, keys: "g b" },
    { label: "My trips", to: "/travel/trips", icon: Luggage },
    { label: "Profile", to: "/profile", icon: User, keys: "g p" },
    { label: "Settings, privacy & devices", to: "/settings", icon: Lock },
    { label: "Your account — bookings, security, invites, disputes, ID", to: "/account", icon: User },
  { label: "Chats — group chats and DMs with friends", to: "/chats", icon: User },
  { label: "Ask Fable — the assistant: find, book and plan", to: "/ask", icon: User },
  { label: "Explore — news, live scores, maps, music, memes, price alerts", to: "/explore", icon: User },
  { label: "Money — unfinished payments, subscriptions, group payments, shop sales", to: "/money", icon: User },
  { label: "Creator studio — profile, links, earnings", to: "/creator-studio", icon: User },
  { label: "Creator tools — highlights, spotlight, verification, clips, music, gifts", to: "/creator-tools", icon: User },
  { label: "Me on Stage — inbox, notes, saved, close friends, feed controls", to: "/stage/me", icon: User },
    { label: "Fable Ops — rotas, HR, pay & team", to: "/ops", icon: BarChart3 },
    { label: "Help centre & contact support", to: "/help", icon: Info },
    { label: "Stories", to: "/stories", icon: Search },
    { label: "Host an event", to: "/events/host", icon: Ticket },
  ] },
  { label: "For partners", items: [
    { label: "Partner dashboard", to: "/partner", icon: Briefcase },
    { label: "Insights", to: "/partner/insights", icon: BarChart3 },
    { label: "Marketing tools", to: "/partner/marketing", icon: Megaphone },
    { label: "List your business", to: "/partner/start", icon: Store },
  ] },
  { label: "Help", items: [
    { label: "About Fable+", to: "/about", icon: Info },
    { label: "Pricing", to: "/pricing", icon: CreditCard },
    { label: "Contact us", to: "/contact", icon: Mail },
    { label: "Accessibility statement", to: "/accessibility", icon: Accessibility },
  ] },
];
const JUMPS = {};
GROUPS.forEach((g) => g.items.forEach((i) => { if (i.keys) JUMPS[i.keys.split(" ")[1]] = i.to; }));

const typing = (el) => el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));

export function openAccessibility() { window.dispatchEvent(new Event("fable:a11y")); }

export function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [help, setHelp] = useState(false);
  const [q, setQ] = useState("");
  const pendingG = useRef(0);
  const [hits, setHits] = useState([]);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setHits([]); return; }
    const t = setTimeout(() => {
      api("/search/all?q=" + encodeURIComponent(term), { auth: false }).then((r) => setHits(r.groups || [])).catch(() => setHits([]));
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((o) => !o); return; }
      if (typing(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "/") { e.preventDefault(); setOpen(true); return; }
      if (e.key === "?") { e.preventDefault(); setHelp(true); return; }
      if (e.key === "g") { pendingG.current = Date.now(); return; }
      if (pendingG.current && Date.now() - pendingG.current < 1200 && JUMPS[e.key]) { e.preventDefault(); pendingG.current = 0; navigate(JUMPS[e.key]); }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("fable:command", onOpen);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("fable:command", onOpen); };
  }, [navigate]);

  const go = (to) => { setOpen(false); setQ(""); navigate(to); };

  return (
    <>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Where to? Type a page or something to find…" value={q} onValueChange={setQ} data-testid="command-input" />
        <CommandList>
          <CommandEmpty>Nothing matches. Press Enter on a search below.</CommandEmpty>
          {q.trim() ? (
            <CommandGroup heading="Search">
              <CommandItem value={"search marketplace " + q} onSelect={() => go("/marketplace?q=" + encodeURIComponent(q.trim()))} data-testid="command-search-marketplace">
                <Search className="mr-2 h-4 w-4" /> Search the marketplace for “{q.trim()}”
              </CommandItem>
              <CommandItem value={"ask yasmin " + q} onSelect={() => go("/yasmin?q=" + encodeURIComponent(q.trim()))}>
                <Sparkles className="mr-2 h-4 w-4" /> Ask Yasmin about “{q.trim()}”
              </CommandItem>
            </CommandGroup>
          ) : null}
          {hits.map((g) => (
            <CommandGroup key={"hit-" + g.key} heading={g.label}>
              {g.items.map((i) => (
                <CommandItem key={g.key + i.id} value={g.label + " " + i.title + " " + q} onSelect={() => go(i.url)} data-testid={"command-hit-" + g.key}>
                  {i.image ? <img src={i.image} alt="" className="mr-2 h-7 w-7 rounded-md object-cover" loading="lazy" /> : <Search className="mr-2 h-4 w-4" />}
                  <span className="min-w-0">
                    <span className="block truncate">{i.title}</span>
                    {i.sub ? <span className="block truncate text-xs text-muted-foreground">{i.sub}</span> : null}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
          {GROUPS.map((g, gi) => (
            <React.Fragment key={g.label}>
              {gi ? <CommandSeparator /> : null}
              <CommandGroup heading={g.label}>
                {g.items.map((i) => (
                  <CommandItem key={i.to} value={g.label + " " + i.label} onSelect={() => go(i.to)}>
                    <i.icon className="mr-2 h-4 w-4" aria-hidden="true" /> {i.label}
                    {i.keys ? <CommandShortcut>{i.keys.toUpperCase()}</CommandShortcut> : null}
                  </CommandItem>
                ))}
              </CommandGroup>
            </React.Fragment>
          ))}
          <CommandSeparator />
          <CommandGroup heading="Settings">
            <CommandItem value="accessibility settings text size contrast motion" onSelect={() => { setOpen(false); openAccessibility(); }}>
              <Accessibility className="mr-2 h-4 w-4" /> Accessibility settings
            </CommandItem>
            <CommandItem value="keyboard shortcuts help" onSelect={() => { setOpen(false); setHelp(true); }}>
              <Keyboard className="mr-2 h-4 w-4" /> Keyboard shortcuts <CommandShortcut>?</CommandShortcut>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
      <Dialog open={help} onOpenChange={setHelp}>
        <DialogContent className="max-w-md" data-testid="shortcuts-dialog">
          <DialogHeader><DialogTitle>Keyboard shortcuts</DialogTitle></DialogHeader>
          <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
            {[["Ctrl / ⌘ + K", "Open the command palette"], ["/", "Open the command palette"], ["?", "Show this list"], ["Tab", "Move between links and buttons"], ["Esc", "Close any dialog"]]
              .concat(GROUPS.flatMap((g) => g.items.filter((i) => i.keys).map((i) => [i.keys.toUpperCase(), "Go to " + i.label])))
              .map(([k, d]) => (
                <React.Fragment key={k + d}>
                  <dt><kbd className="rounded border px-1.5 py-0.5 font-mono text-xs">{k}</kbd></dt>
                  <dd className="text-muted-foreground">{d}</dd>
                </React.Fragment>
              ))}
          </dl>
        </DialogContent>
      </Dialog>
    </>
  );
}
